import axios from 'axios';

// Prefer explicit env override, otherwise use same-origin proxy path.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Backend capability flags (cached per page load) to avoid repeated 404/405 spam.
let activityLogsEndpointUnavailable = false;
let batchUsageEndpointUnavailable = false;

// Attach token to every request
api.interceptors.request.use((config) => {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed?.token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${parsed.token}`;
      }
    }
  } catch {
    // ignore parsing errors
  }
  return config;
});

// Sanitize error responses to prevent API URL exposure
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Session expired / unauthorized — clear local session so UI redirects to login
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 401 && detail && String(detail).toLowerCase().includes('expired')) {
      try {
        localStorage.removeItem('user');
      } catch {
        // ignore
      }
      if (typeof window !== 'undefined' && !window.location.pathname.includes('login')) {
        window.location.assign('/login');
      }
    }

    // Deep sanitize error to prevent API URL exposure in console
    if (error.config) {
      // Sanitize the config object (which axios uses for logging)
      if (error.config.baseURL) {
        error.config.baseURL = '[API_URL]';
      }
      if (error.config.url && typeof error.config.url === 'string') {
        error.config.url = error.config.url.replace(API_BASE_URL, '/api');
      }
      // Sanitize the full url in config
      if (error.config.url && error.config.baseURL) {
        Object.defineProperty(error.config, 'fullUrl', {
          value: '[API_URL]' + error.config.url,
          writable: false
        });
      }
    }
    
    // Sanitize error message
    if (error.message && typeof error.message === 'string') {
      error.message = error.message.replace(API_BASE_URL, '[API_URL]');
      error.message = error.message.replace(/https?:\/\/[^\s]+/g, '[API_URL]');
    }
    
    // Sanitize response config if present
    if (error.response?.config) {
      if (error.response.config.baseURL) {
        error.response.config.baseURL = '[API_URL]';
      }
      if (error.response.config.url) {
        error.response.config.url = error.response.config.url.replace(API_BASE_URL, '/api');
      }
    }
    
    // Sanitize request object
    if (error.request) {
      const requestCopy = { ...error.request };
      if (requestCopy.responseURL) {
        requestCopy.responseURL = requestCopy.responseURL.replace(API_BASE_URL, '[API_URL]');
      }
      Object.defineProperty(error, 'request', {
        value: requestCopy,
        writable: false,
        configurable: true
      });
    }
    
    return Promise.reject(error);
  }
);

export const login = async (username, password) => {
  const response = await api.post('/login', { username, password });
  return response.data;
};

export const clientLogin = async (clientId, password) => {
  const response = await api.post('/client-login', { username: clientId, password });
  return response.data;
};

export const getSchedules = async () => {
  const response = await api.get('/schedules');
  return response.data;
};

export const createSchedule = async (schedule) => {
  const response = await api.post('/schedules', schedule);
  return response.data;
};

export const updateSchedule = async (scheduleId, schedule) => {
  const response = await api.put(`/schedules/${scheduleId}`, schedule);
  return response.data;
};

export const deleteSchedule = async (scheduleId) => {
  const response = await api.delete(`/schedules/${scheduleId}`);
  return response.data;
};

export const getDispensers = async (params = {}) => {
  const response = await api.get('/dispensers', { params });
  return response.data;
};

export const getDispenser = async (dispenserId) => {
  const response = await api.get(`/dispensers/${dispenserId}`);
  return response.data;
};

export const createDispenser = async (dispenser) => {
  const response = await api.post('/dispensers', dispenser);
  return response.data;
};

export const updateDispenser = async (dispenserId, dispenser) => {
  const response = await api.put(`/dispensers/${dispenserId}`, dispenser);
  return response.data;
};

export const deleteDispenser = async (dispenserId) => {
  const response = await api.delete(`/dispensers/${dispenserId}`);
  return response.data;
};

export const assignSchedule = async (dispenserId, scheduleId) => {
  const response = await api.post(`/dispensers/${dispenserId}/assign-schedule`, {
    schedule_id: scheduleId
  });
  return response.data;
};

export const logRefill = async (dispenserId, refillData, options = {}) => {
  /**
   * Uber-style write: stable client id + long timeout + safe retries.
   * Retries reuse the same refill id so the server dedupes instead of double-writing.
   */
  const timeout = options.timeout ?? 60_000;
  const maxAttempts = options.maxAttempts ?? 3;
  const onAttempt = typeof options.onAttempt === 'function' ? options.onAttempt : null;

  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (onAttempt) onAttempt(attempt, maxAttempts);
    try {
      const response = await api.post(`/dispensers/${dispenserId}/refill`, refillData, {
        timeout,
      });
      return response.data;
    } catch (err) {
      lastError = err;
      const status = err?.response?.status;
      const code = err?.code;
      const isTimeout = code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
      const isNetwork = !err?.response || code === 'ERR_NETWORK';
      const isRetryable =
        isTimeout || isNetwork || status === 408 || status === 429 || (status >= 500 && status < 600);

      // 4xx (except timeout/rate-limit) = permanent; don't retry
      if (!isRetryable) throw err;
      if (attempt >= maxAttempts) break;

      // Exponential backoff: 1s, 2s
      await new Promise((r) => setTimeout(r, 1000 * attempt));
    }
  }
  throw lastError;
};

export const getRefillLogs = async (params = {}) => {
  const response = await api.get('/refill-logs', { params });
  return response.data;
};

const downloadCsv = async (path, filename) => {
  const response = await api.get(path, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadRefillLogsExport = () => downloadCsv('/refill-logs/export', 'refill-logs.csv');

export const downloadRefillImportTemplate = () => downloadCsv('/refill-logs/import-template', 'refill-backfill-template.csv');

export const importRefillLogs = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post('/refill-logs/import', form);
  return response.data;
};

export const calculateUsage = async (dispenserId) => {
  const response = await api.get(`/dispensers/${dispenserId}/usage-calculation`);
  return response.data;
};

// Read usage from DB materialization via batch-usage.
// Parallel chunks — never wait on sequential round-trips for oil levels.
export const batchCalculateUsage = async (dispenserIds, options = {}) => {
  if (!dispenserIds || dispenserIds.length === 0) {
    return {};
  }

  // List views: serve cache immediately (no sync recompute). Detail views can pass true.
  const refreshMisses = options.refresh_misses === true;
  const MAX = 400;

  if (dispenserIds.length > MAX) {
    const chunks = [];
    for (let i = 0; i < dispenserIds.length; i += MAX) {
      chunks.push(dispenserIds.slice(i, i + MAX));
    }
    const parts = await Promise.all(chunks.map((chunk) => batchCalculateUsage(chunk, options)));
    return Object.assign({}, ...parts);
  }

  if (batchUsageEndpointUnavailable) {
    return {};
  }

  try {
    const response = await api.post('/dispensers/batch-usage', {
      dispenser_ids: dispenserIds,
      refresh_misses: refreshMisses,
    });
    return response.data;
  } catch (err) {
    if (err?.response?.status === 404 || err?.response?.status === 405) {
      batchUsageEndpointUnavailable = true;
      return {};
    }
    throw err;
  }
};

export const getClients = async () => {
  const response = await api.get('/clients');
  return response.data;
};

export const getClient = async (clientId) => {
  const response = await api.get(`/clients/${clientId}`);
  return response.data;
};

export const createClient = async (client) => {
  const response = await api.post('/clients', client);
  return response.data;
};

export const updateClient = async (clientId, client) => {
  const response = await api.put(`/clients/${clientId}`, client);
  return response.data;
};

export const deleteClient = async (clientId) => {
  const response = await api.delete(`/clients/${clientId}`);
  return response.data;
};

export const getUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

export const createUser = async (user) => {
  const response = await api.post('/users', user);
  return response.data;
};

export const updateUser = async (username, user) => {
  const response = await api.put(`/users/${username}`, user);
  return response.data;
};

export const deleteUser = async (username) => {
  const response = await api.delete(`/users/${username}`);
  return response.data;
};

// Technician Assignments
export const getTechnicianAssignments = async (technician = null, status = null) => {
  let url = '/technician-assignments';
  const params = new URLSearchParams();
  if (technician) params.append('technician', technician);
  if (status) params.append('status', status);
  if (params.toString()) url += `?${params.toString()}`;
  const response = await api.get(url);
  return response.data;
};

export const createTechnicianAssignment = async (assignment) => {
  const response = await api.post('/technician-assignments', assignment);
  return response.data;
};

export const updateTechnicianAssignment = async (assignmentId, assignmentUpdate) => {
  const response = await api.put(`/technician-assignments/${assignmentId}`, assignmentUpdate);
  return response.data;
};

export const deleteTechnicianAssignment = async (assignmentId) => {
  const response = await api.delete(`/technician-assignments/${assignmentId}`);
  return response.data;
};

export const completeAssignment = async (assignmentId, completionData = {}) => {
  const response = await api.post(`/technician-assignments/${assignmentId}/complete`, completionData);
  return response.data;
};

export const logVisitAction = async (assignmentId, payload) => {
  const response = await api.post(`/technician-assignments/${assignmentId}/visit-action`, payload);
  return response.data;
};

export const downloadVisitReport = async (assignmentId, filename) => {
  const response = await api.get(`/technician-assignments/${assignmentId}/visit-report`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `visit-report-${assignmentId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return true;
};

export const reviveMachineOil = async (dispenserId, payload) => {
  const response = await api.post(`/dispensers/${dispenserId}/revive`, payload);
  return response.data;
};

export const getActivityLogs = async (params = {}) => {
  const {
    limit = 1000,
    offset = 0,
    action = null,
    user = null,
    role = null,
    entity_type = null,
    start_date = null,
    end_date = null,
  } = params;
  
  const queryParams = new URLSearchParams();
  queryParams.append('limit', limit);
  queryParams.append('offset', offset);
  if (action) queryParams.append('action', action);
  if (user) queryParams.append('user', user);
  if (role) queryParams.append('role', role);
  if (entity_type) queryParams.append('entity_type', entity_type);
  if (start_date) queryParams.append('start_date', start_date);
  if (end_date) queryParams.append('end_date', end_date);
  
  if (activityLogsEndpointUnavailable) return [];

  try {
    const response = await api.get(`/activity-logs?${queryParams.toString()}`);
    return response.data;
  } catch (err) {
    // Older backend deployments may not have activity logs endpoint yet.
    if (err?.response?.status === 404) {
      activityLogsEndpointUnavailable = true;
      return [];
    }
    throw err;
  }
};

export const getActivityStats = async () => {
  const response = await api.get('/activity-logs/stats');
  return response.data;
};

export const getTechnicianStats = async (technicianUsername, startDate = null, endDate = null) => {
  let url = `/technician-stats/${technicianUsername}`;
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  if (params.toString()) url += `?${params.toString()}`;
  const response = await api.get(url);
  return response.data;
};

// ============================================================================
// COGS + Client Billing (Supabase-backed)
// ============================================================================

export const getCogsMachineCosts = async () => {
  const response = await api.get('/cogs/machine-costs');
  return response.data;
};

export const saveCogsMachineCost = async (payload) => {
  const response = await api.put('/cogs/machine-costs', payload);
  return response.data;
};

export const getCogsOilPrices = async () => {
  const response = await api.get('/cogs/oil-prices');
  return response.data;
};

export const saveCogsOilPrice = async (payload) => {
  const response = await api.put('/cogs/oil-prices', payload);
  return response.data;
};

export const deleteCogsOilPrice = async (oilCode) => {
  const response = await api.delete(`/cogs/oil-prices/${encodeURIComponent(oilCode)}`);
  return response.data;
};

export const getClientBilling = async (clientId) => {
  const response = await api.get(`/clients/${encodeURIComponent(clientId)}/billing`);
  return response.data;
};

export const getClientLifetimeFinancials = async (clientId) => {
  const response = await api.get(`/clients/${encodeURIComponent(clientId)}/lifetime-financials`);
  return response.data;
};

export const getClientOilUsageSummary = async (clientId) => {
  const response = await api.get(`/clients/${encodeURIComponent(clientId)}/oil-usage-summary`);
  return response.data;
};

export const saveClientBilling = async (clientId, payload) => {
  const response = await api.put(`/clients/${encodeURIComponent(clientId)}/billing`, payload);
  return response.data;
};

export const saveClientPricePeriods = async (clientId, periods) => {
  const response = await api.put(`/clients/${encodeURIComponent(clientId)}/price-periods`, periods);
  return response.data;
};

export const getManagementDashboard = async (params = {}) => {
  const {
    year,
    month,
    start_year = null,
    start_month = null,
    client_id = null,
    client_ids = null,
    billing_model = null,
    machine_type = null,
    projection_months = 6,
  } = params;
  const query = new URLSearchParams();
  query.append('year', year);
  query.append('month', month);
  if (start_year) query.append('start_year', start_year);
  if (start_month) query.append('start_month', start_month);
  const selectedClients = Array.isArray(client_ids) && client_ids.length
    ? client_ids
    : (client_id ? [client_id] : []);
  selectedClients.forEach((id) => {
    if (id) query.append('client_id', id);
  });
  if (billing_model) query.append('billing_model', billing_model);
  if (machine_type) query.append('machine_type', machine_type);
  query.append('projection_months', projection_months);
  const response = await api.get(`/management/dashboard?${query.toString()}`);
  return response.data;
};

